import { useState } from 'react';
import { MoreHorizontal, Trash2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import {
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from '../ui/menu';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
} from '../ui/dialog';
import { BtcApplicationForm } from './btc-application-form';
import { useAppData } from '../../contexts/app-data-context';
import { formatCurrency } from '../../lib/format';
import type { BtcApplication } from '../../types';

function formatDate(date: Date): string {
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatBtc(value: number): string {
  return value.toFixed(8).replace(/\.?0+$/, '').padEnd(1, '0');
}

interface BtcCaixinhaCardProps {
  applications: BtcApplication[];
  onAdd: (data: Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  onUpdate: (id: string, data: Partial<Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function BtcCaixinhaCard({ applications, onAdd, onUpdate, onDelete }: BtcCaixinhaCardProps) {
  const { bitcoinPrice: price, loadingBitcoinPrice: priceLoading, bitcoinPriceError: priceError } = useAppData();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BtcApplication | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BtcApplication | null>(null);
  const [listExpanded, setListExpanded] = useState(false);

  const sortedApps = [...applications].sort((a, b) => b.date.getTime() - a.date.getTime());

  const totalBtc = applications.reduce((s, a) => s + a.btcAmount - a.feeBtc, 0);
  const totalPaidBrl = applications.reduce((s, a) => s + a.paidBrl, 0);

  const currentBrl = price ? totalBtc * price.brl : null;
  const currentUsd = price ? totalBtc * price.usd : null;
  const plBrl = currentBrl !== null ? currentBrl - totalPaidBrl : null;
  const plPct = plBrl !== null && totalPaidBrl > 0 ? (plBrl / totalPaidBrl) * 100 : null;

  const handleFormSubmit = async (data: Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>) => {
    if (editing) {
      await onUpdate(editing.id, data);
    } else {
      await onAdd(data);
    }
    setDialogOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (deleteTarget) {
      await onDelete(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border-2 border-[#F7931A]/40 bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">₿</span>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Bitcoin</h3>
            <p className="text-xs text-muted-foreground">
              {applications.length === 0 ? 'Nenhuma aplicação' : `${applications.length} aplicação${applications.length > 1 ? 'ões' : ''}`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="flex items-center gap-1 rounded-md border border-[#F7931A]/40 px-2 py-1 text-xs text-[#F7931A] hover:bg-[#F7931A]/10 transition-colors"
        >
          <Plus className="size-3" /> Adicionar
        </button>
      </div>

      {/* BTC total */}
      <div>
        <span className="text-xs text-muted-foreground">Total BTC (líquido)</span>
        <p className="font-mono text-lg font-bold text-[#F7931A]">₿ {formatBtc(totalBtc)}</p>
      </div>

      {/* Current value */}
      <div className="flex flex-col gap-1 rounded-lg bg-[#F7931A]/5 px-3 py-2">
        {priceLoading && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <RefreshCw className="size-3 animate-spin" /> Carregando preço...
          </div>
        )}
        {priceError && (
          <span className="text-xs text-destructive">Erro ao buscar preço</span>
        )}
        {currentBrl !== null && (
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Valor atual (BRL)</span>
            <span className="text-sm font-semibold text-foreground">R$ {formatCurrency(currentBrl)}</span>
          </div>
        )}
        {currentUsd !== null && (
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Valor atual (USD)</span>
            <span className="text-sm font-semibold text-foreground">$ {formatCurrency(currentUsd)}</span>
          </div>
        )}
        {price && (
          <div className="flex items-baseline justify-between border-t border-[#F7931A]/20 mt-1 pt-1">
            <span className="text-xs text-muted-foreground">1 BTC</span>
            <span className="text-xs text-muted-foreground">R$ {formatCurrency(price.brl)}</span>
          </div>
        )}
      </div>

      {/* P&L */}
      {applications.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Total investido</span>
            <span className="text-xs font-medium text-foreground">R$ {formatCurrency(totalPaidBrl)}</span>
          </div>
          {plBrl !== null && plPct !== null && (
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold text-foreground">Resultado</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${plBrl >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {plBrl >= 0 ? '+' : ''}R$ {formatCurrency(plBrl)}
                </span>
                <span className={`w-14 text-right text-xs font-semibold ${plBrl >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {plBrl >= 0 ? '+' : ''}{plPct.toFixed(2)}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Applications list */}
      {applications.length > 0 && (
        <div className="border-t border-border pt-3">
          <span className="text-xs font-medium text-muted-foreground mb-2 block">
            Aplicações ({sortedApps.length})
          </span>
          <ul className="flex flex-col gap-1">
            {(listExpanded ? sortedApps : sortedApps.slice(0, 3)).map((app) => (
              <li
                key={app.id}
                className="flex items-center justify-between gap-2 rounded-md bg-surface px-2 py-1.5"
              >
                <span className="text-xs text-muted-foreground shrink-0">{formatDate(app.date)}</span>
                <span className="font-mono text-xs font-medium text-[#F7931A] flex-1 text-right">
                  ₿ {formatBtc(app.btcAmount)}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  R$ {formatCurrency(app.paidBrl)}
                </span>
                <MenuRoot>
                  <MenuTrigger
                    render={
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <MoreHorizontal className="size-3.5" />
                      </button>
                    }
                  />
                  <MenuPortal>
                    <MenuPositioner sideOffset={4}>
                      <MenuPopup>
                        <MenuItem onClick={() => { setEditing(app); setDialogOpen(true); }}>
                          <Pencil className="size-4" /> Editar
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem
                          onClick={() => setDeleteTarget(app)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-4" /> Excluir
                        </MenuItem>
                      </MenuPopup>
                    </MenuPositioner>
                  </MenuPortal>
                </MenuRoot>
              </li>
            ))}
          </ul>
          {sortedApps.length > 3 && (
            <button
              type="button"
              onClick={() => setListExpanded((v) => !v)}
              className="mt-1 w-full text-xs text-primary hover:underline text-center"
            >
              {listExpanded ? 'Esconder' : `Ver tudo (${sortedApps.length})`}
            </button>
          )}
        </div>
      )}

      {/* Add / Edit dialog */}
      <DialogRoot
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) { setDialogOpen(false); setEditing(null); }
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar aplicação BTC' : 'Nova aplicação BTC'}</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <BtcApplicationForm
                onSubmit={handleFormSubmit}
                onClose={() => { setDialogOpen(false); setEditing(null); }}
                initialData={editing}
              />
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>

      {/* Delete confirmation dialog */}
      <DialogRoot
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <p className="text-sm text-foreground-subtle">
                Excluir aplicação de{' '}
                <strong className="text-foreground font-mono">₿ {deleteTarget ? formatBtc(deleteTarget.btcAmount) : ''}</strong>{' '}
                em {deleteTarget ? formatDate(deleteTarget.date) : ''}?
              </p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>
                  Cancelar
                </Button>
                <Button variant="destructive" className="flex-1" onClick={handleDeleteConfirm}>
                  Excluir
                </Button>
              </div>
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </div>
  );
}
